import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import {
  GalleryGrid,
  GalleryLightbox,
  GallerySharedStyles,
  type GalleryImage,
} from './_galleryShared';

/**
 * Unified photo gallery — all property photos arranged in a simple,
 * uniform three-column grid (two columns on tablet, single column on
 * mobile). Each tile opens the shared lightbox.
 */
export function PhotoGallery() {
  const images = useCollectionBinding<GalleryImage>('photogallery.images', {
    collection: 'photo_gallery',
  });

  const photoList = useMemo(() => {
    const list = images && images.length > 0 ? images : [];
    const excludedTitles = new Set(['other', 'floorplan', 'logo']);
    return list.filter(
      (img) =>
        Boolean(img.image) &&
        !excludedTitles.has((img.title || '').toLowerCase().trim()),
    );
  }, [images]);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (photoList.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-constrained py-8 md:py-12">
        <GalleryGrid images={photoList} onSelect={setSelectedIndex} />
      </div>

      <GalleryLightbox
        images={photoList}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PhotoGallery',
  displayName: 'Photo Gallery',
  tokens: {},
  collectionBindings: {
    'photogallery.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      description: 'All photos displayed in the alternating mosaic gallery layout.',
    },
  },
};

(PhotoGallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PhotoGallery;
